import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Target, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { mission, coreValues } from '@/data/mock';
import { cn } from '@/lib/utils';

export const AboutSection = ({ className }) => {
  const highlights = [
    {
      icon: Shield,
      title: 'Safety First',
      description: 'Zero compromise on protecting our people and communities.',
    },
    {
      icon: Target,
      title: 'Engineering Excellence',
      description: 'Technical precision and quality craftsmanship in every project.',
    },
    {
      icon: Users,
      title: 'Community Impact',
      description: 'Building infrastructure that strengthens the places we live.',
    },
  ];

  return (
    <section className={cn('py-20 bg-white', className)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div>
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Who We Are</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
              Building Infrastructure That Stands the Test of Time
            </h2>
            <p className="mt-6 text-lg text-gray-600 leading-relaxed">
              {mission}
            </p>

            {/* Highlights */}
            <div className="mt-10 space-y-6">
              {highlights.map((item, index) => (
                <div key={index} className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{item.title}</h4>
                    <p className="text-gray-600 text-sm mt-1">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <Link to="/about">
                <Button className="bg-red-600 hover:bg-red-700 text-white px-8 group">
                  Learn More About Us
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Image Grid */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxjaXZpbCUyMGVuZ2luZWVyaW5nfGVufDB8fHx8MTc2NDE0MzU2OHww&ixlib=rb-4.1.0&q=85"
                    alt="Construction team"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="https://images.unsplash.com/photo-1580810734915-93a368ed2073?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxjaXZpbCUyMGVuZ2luZWVyaW5nfGVufDB8fHx8MTc2NDE0MzU2OHww&ixlib=rb-4.1.0&q=85"
                    alt="Steel structure"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="https://images.unsplash.com/photo-1529926691761-20fb82067c71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxpbmZyYXN0cnVjdHVyZXxlbnwwfHx8fDE3NjQxNDM1NzN8MA&ixlib=rb-4.1.0&q=85"
                    alt="Bridge infrastructure"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="https://images.unsplash.com/photo-1485083269755-a7b559a4fe5e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxjaXZpbCUyMGVuZ2luZWVyaW5nfGVufDB8fHx8MTc2NDE0MzU2OHww&ixlib=rb-4.1.0&q=85"
                    alt="Construction cranes"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
            {/* Red accent */}
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-red-600 rounded-2xl -z-10" />
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-red-100 rounded-2xl -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
